const PREFS_KEY = 'river_notification_prefs';

export interface Toast {
  id: string;
  message: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  /** Override auto-dismiss duration in ms. Falls back to prefs.duration. */
  duration?: number;
  actionUrl?: string;
  data?: any;
}

export interface NotificationPrefs {
  autoDismiss: boolean;
  duration: 5 | 10 | 30;
}

const DEFAULT_PREFS: NotificationPrefs = {
  autoDismiss: true,
  duration: 5,
};

type ToastListener = (toasts: Toast[], prefs: NotificationPrefs) => void;

class ToastStore {
  private toasts: Toast[] = [];
  private listeners: Set<ToastListener> = new Set();
  private prefs: NotificationPrefs = { ...DEFAULT_PREFS };

  constructor() {
    try {
      const saved = localStorage.getItem(PREFS_KEY);
      if (saved) this.prefs = { ...DEFAULT_PREFS, ...JSON.parse(saved) };
    } catch {
      // ignore
    }
  }

  add(toast: Omit<Toast, 'id'> & { id?: string }): string {
    const id = toast.id ?? `toast_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    // Avoid duplicates
    if (this.toasts.some(t => t.id === id)) return id;
    this.toasts = [{ ...toast, id }, ...this.toasts];
    this._notify();
    return id;
  }

  remove(id: string) {
    this.toasts = this.toasts.filter(t => t.id !== id);
    this._notify();
  }

  clear() {
    this.toasts = [];
    this._notify();
  }

  getAll(): Toast[] {
    return [...this.toasts];
  }

  subscribe(listener: ToastListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getPrefs(): NotificationPrefs {
    return { ...this.prefs };
  }

  setPrefs(update: Partial<NotificationPrefs>) {
    this.prefs = { ...this.prefs, ...update };
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify(this.prefs));
    } catch {
      // ignore
    }
    this._notify();
  }

  private _notify() {
    const toasts = this.getAll();
    const prefs = this.getPrefs();
    this.listeners.forEach(fn => fn(toasts, prefs));
  }
}

// Preserve the singleton across Vite HMR module re-executions (same pattern as
// wsClientSingleton.ts / wsTokenRefresher.ts). Without this, an HMR reload of
// this module creates a fresh ToastStore with empty `listeners`, but
// ToastStack.tsx's already-mounted `toastStore.subscribe()` effect (empty dep
// array, so it never re-runs) stays bound to the OLD instance — while
// notificationService.ts's `import('../stores/toastStore').then(...)` picks up
// the NEW instance on every call. `.add()` then silently writes into a store
// nobody is listening to: no error, no visible symptom, toasts just stop
// appearing for the rest of the session.
const _TOAST_STORE_KEY = Symbol.for('__river_toast_store__');
const _toastStoreGlobal = globalThis as unknown as { [_TOAST_STORE_KEY]?: ToastStore };
export const toastStore: ToastStore =
  _toastStoreGlobal[_TOAST_STORE_KEY] ?? (_toastStoreGlobal[_TOAST_STORE_KEY] = new ToastStore());
